import { usePermissionContext } from '@/contexts/PermissionContext'
import { PERMISSION_CODES } from '@/types/auth'

/**
 * Hook for checking user permissions with convenience methods
 * 
 * @example
 * const { canViewOrders, canCreateOrders, hasPermission } = usePermissions()
 * 
 * if (canViewOrders()) {
 *   // Show orders page
 * }
 */
export function usePermissions() {
  const {
    permissions,
    activeProfile,
    profiles,
    isLoadingPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOrganization,
    isOperation,
    isProvider,
    isClient,
    isBusinessClient,
    isSuperuser,
    activeProfileId,
    setActiveProfile,
  } = usePermissionContext()

  // ============================================
  // Orders permissions
  // ============================================

  /**
   * Check if user can view orders (organization or operations)
   */
  const canViewOrders = (): boolean => {
    return hasAnyPermission([
      PERMISSION_CODES.ORDERS_VIEW_ORGANIZATION,
      PERMISSION_CODES.ORDERS_VIEW_ALL,
      PERMISSION_CODES.ORDERS_VIEW_OWN,
      PERMISSION_CODES.ORDERS_VIEW_ASSIGNED,
    ])
  }

  /**
   * Check if user can create new orders
   */
  const canCreateOrders = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORDERS_CREATE)
  }

  /**
   * Check if user can manage all orders (operations)
   */
  const canManageAllOrders = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORDERS_MANAGE_ALL)
  }

  /**
   * Check if user can accept orders/services (provider)
   */
  const canAcceptOrders = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORDERS_ACCEPT)
  }

  /**
   * Check if user can complete orders/services (provider)
   */
  const canCompleteOrders = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORDERS_COMPLETE)
  }
  
  /**
   * Check if user can cancel their own orders (client)
   */
  const canCancelOwnOrders = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORDERS_CANCEL_OWN)
  }

  // ============================================
  // Providers permissions
  // ============================================

  /**
   * Check if user can manage providers
   */
  const canManageProviders = (): boolean => {
    return hasAnyPermission([
      PERMISSION_CODES.PROVIDERS_MANAGE,
      PERMISSION_CODES.PROVIDERS_MANAGE_ORGANIZATION,
    ])
  }

  /**
   * Check if user can view providers list
   */
  const canViewProviders = (): boolean => {
    return hasAnyPermission([
      PERMISSION_CODES.PROVIDERS_MANAGE,
      PERMISSION_CODES.PROVIDERS_MANAGE_ORGANIZATION,
    ])
  }
  
  /**
   * Check if user can manage availability (provider)
   */
  const canManageAvailability = (): boolean => {
    return hasPermission(PERMISSION_CODES.AVAILABILITY_MANAGE)
  }

  // ============================================
  // Financials/Reports permissions
  // ============================================

  /**
   * Check if user can view financials and reports
   */
  const canViewFinancials = (): boolean => {
    return hasAnyPermission([
      PERMISSION_CODES.REPORTS_VIEW,
      PERMISSION_CODES.REPORTS_VIEW_ORGANIZATION,
    ])
  }

  /**
   * Check if user can approve payouts (organization level)
   */
  const canApprovePayouts = (): boolean => {
    return hasPermission(PERMISSION_CODES.REPORTS_VIEW_ORGANIZATION)
  }

  // ============================================
  // System/Settings permissions
  // ============================================

  /**
   * Check if user can configure system settings
   */
  const canConfigureSystem = (): boolean => {
    return hasPermission(PERMISSION_CODES.SYSTEM_CONFIGURE)
  }

  /**
   * Check if user can manage their organization
   */
  const canManageOrganization = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORGANIZATION_MANAGE_OWN)
  }
  
  /**
   * Check if user can manage all organizations (operations)
   */
  const canManageAllOrganizations = (): boolean => {
    return hasPermission(PERMISSION_CODES.ORGANIZATIONS_MANAGE)
  }

  // ============================================
  // Users permissions
  // ============================================

  /**
   * Check if user can manage users
   */
  const canManageUsers = (): boolean => {
    return hasPermission(PERMISSION_CODES.USERS_VIEW_ALL)
  }

  /**
   * Check if user can view users
   */
  const canViewUsers = (): boolean => {
    return hasPermission(PERMISSION_CODES.USERS_VIEW_ALL)
  }
  
  // ============================================
  // Beneficiary permissions
  // ============================================

  /**
   * Check if user can manage beneficiaries (client)
   */
  const canManageBeneficiaries = (): boolean => {
    return hasPermission(PERMISSION_CODES.BENEFICIARIES_MANAGE)
  }
  
  // ============================================
  // Profile permissions
  // ============================================

  /**
   * Check if user can view their own profile
   */
  const canViewOwnProfile = (): boolean => {
    return hasPermission(PERMISSION_CODES.PROFILE_VIEW_OWN)
  }
  
  /**
   * Check if user can update their own profile
   */
  const canUpdateOwnProfile = (): boolean => {
    return hasPermission(PERMISSION_CODES.PROFILE_UPDATE_OWN)
  }

  // ============================================
  // Dashboard access
  // ============================================

  /**
   * Check if user can access the main dashboard
   */
  const canAccessDashboard = (): boolean => {
    // Any authenticated user with view permissions can access dashboard
    return canViewOrders() || canViewProviders() || canViewFinancials()
  }

  return {
    // Raw permission data
    permissions,
    activeProfile,
    profiles,
    activeProfileId,
    
    // Loading state
    isLoadingPermissions,

    // Base permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Role checks
    isOrganization,
    isOperation,
    isProvider,
    isClient,
    isBusinessClient,
    isSuperuser,

    // Profile management
    setActiveProfile,

    // Convenience methods - Orders
    canViewOrders,
    canCreateOrders,
    canManageAllOrders,
    canAcceptOrders,
    canCompleteOrders,
    canCancelOwnOrders,

    // Convenience methods - Providers
    canManageProviders,
    canViewProviders,
    canManageAvailability,

    // Convenience methods - Financials
    canViewFinancials,
    canApprovePayouts,

    // Convenience methods - System
    canConfigureSystem,
    canManageOrganization,
    canManageAllOrganizations,

    // Convenience methods - Users
    canManageUsers,
    canViewUsers,
    
    // Convenience methods - Beneficiaries
    canManageBeneficiaries,
    
    // Convenience methods - Profile
    canViewOwnProfile,
    canUpdateOwnProfile,

    // Convenience methods - Dashboard
    canAccessDashboard,
  }
}
